import { FunctionComponent, useEffect, useRef, useState } from 'react'
import { useFrame } from '@react-three/fiber'
import './MotionControls.css'
import GyroCameraBehaviour from './GyroCameraBehaviour'
import { Vector2 } from 'three'
import * as THREE from 'three'

/*
  world range = the range in the the real world of the device
  virtual range = the range in the virtual world that "world range" maps on to
    example:
    worldRange.y is 90
    virtualRange.y is 45
    every 90 degrees in the real world maps onto 90 degress in the virtual work

  hardVirtualRange clamps that range. So if y is 90, then we can't rotate past 90, it would get clamped
*/

const searchParams = new URLSearchParams(window.location.search)
function tryGetURLParamterAsNumber (_name:string, _defaultValue:number) : number {
  const value = searchParams.get(_name)
  if (value == null || value === 'false' || value === 'true') {
    return _defaultValue
  }
  return parseFloat(value)
}

// So we can find the right FEEL, this esables us to test different values
const worldRangeX = tryGetURLParamterAsNumber('WorldRangeX', 360)
const worldRangeY = tryGetURLParamterAsNumber('WorldRangeY', 360)

const virtualRangeX = tryGetURLParamterAsNumber('VirtualRangeX', 360)
const virtualRangeY = tryGetURLParamterAsNumber('VirtualRangeY', 360)

const hardVirtualRangeX = tryGetURLParamterAsNumber('HardVirtualRangeX', 360)
const hardVirtualRangeY = tryGetURLParamterAsNumber('HardVirtualRangeY', 360)

type MotionControlsProps = {
  children? : JSX.Element | JSX.Element[] | null
}

const MotionControls: FunctionComponent<MotionControlsProps> = (props) => {
  const yAxisRotationRef = useRef<THREE.Object3D>()
  const xAxisRotationRef = useRef<THREE.Object3D>()
  const [gyroController, setGyroController] = useState<GyroCameraBehaviour>()

  useEffect(() => {
    const gyroParams = {
      worldRange: new Vector2(
        THREE.MathUtils.degToRad(worldRangeX),
        THREE.MathUtils.degToRad(worldRangeY)),
      virtualRange: new Vector2(
        THREE.MathUtils.degToRad(virtualRangeX),
        THREE.MathUtils.degToRad(virtualRangeY)),
      hardVirtualRange: new Vector2(
        THREE.MathUtils.degToRad(hardVirtualRangeX),
        THREE.MathUtils.degToRad(hardVirtualRangeY)
      ),

      yRotationTarget: yAxisRotationRef.current ?? new THREE.Object3D(),
      xRotationTarget: xAxisRotationRef.current ?? new THREE.Object3D()
    }

    const gryo = new GyroCameraBehaviour(gyroParams)
    gryo.OnEnable()

    setGyroController(gryo)

    return () => {
      gyroController?.Dispose()
    }
  }, [])

  useFrame((_state, _deltaTime) => {
    if (gyroController !== null) gyroController?.OnUpdate(_deltaTime)
  })

  return (
    <object3D ref={yAxisRotationRef}>
      <object3D ref={xAxisRotationRef}>
        {props.children}
      </object3D>
    </object3D>
  )
}

export default MotionControls
